import { useState } from "react";

import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { InboundLayout } from "ui-components/Layout";
import { InboundDetailAddButton, InboundDetailEditButton } from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./so-schema";
import { detailRender, detailValues, detailValidation } from "./so-details-schema";
import SO from "./components/SO";
import SODetail from "./components/SODetail";
import { soColumns } from "./columns";

import { Success, Error_ } from "ui-components/Alert";
import { InfoDialog } from "ui-components/Alert";
import { InboundSaveButton } from "ui-components/Form";

import { skuValidation } from "layouts/views/common/validation";
import { dateValidation } from "layouts/views/common/validation";

import { CancelAction } from "layouts/views/components/Grid";
import { StyledDataGrid } from "layouts/views/components/Grid";
import { editSO } from "services/data.outbound.service";
import { getSO } from "services/data.outbound.service";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { LoadingDialog } from "ui-components/Alert";
import { InboundHeaderCard } from "ui-components/Layout";
import { statusLabel, statusObj } from "layouts/views/common/enum";
import { deleteNewDetail } from "layouts/views/inbound/common/common";
import { cancelDetail } from "layouts/views/inbound/common/common";
import { getTotalQuantity } from "layouts/views/inbound/common/common";
import { skuValidation_ } from "layouts/views/inbound/common/common";
import { updateDetail } from "layouts/views/inbound/common/common";

function EditSO() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, orderNumber, customerCode } = location.state;
  const [formValues, setFormValues] = useState(headerValues);
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const [click, setClick] = useState(false)

  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const EditSOMutation = useMutation(editSO, {});

  const { isFetching } = useQuery({
    queryKey: ["so", { orderNumber, customerCode }],
    queryFn: () => getSO({orderId, orderNumber, customerCode}),
    onSuccess: (data) => {
      console.log(data.data.data);
      const { soDetails, status, ...soValues } = data.data.data;
      setFormValues({ ...soValues, status: statusLabel[status] });
      setDetails(soDetails.map((d) => ({ ...d, status: statusLabel[d.status] })));
    },
    onError,
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
    trigger,
  } = form;

   const shipmentType = watch("shipmentType");

  const headerProps = {
    headerRender,
    customerCode,
    shipmentType,
    control,
    setValue,
    getValues,
    errors,
    mode: "EDIT",
  };

  const detailProps = { details, setDetails, values: { customerCode } };

  const onSubmit = async () => {
    setClick(true)
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo(0, 0);
      setClick(false)
      return;
    }

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid SO Details" onChange={setAlert} />);
      setClick(false)
      return;
    }

    const data = getValues();

    const req = {
      ...data,
      SODetails: details.map((d) => ({
        ...d,
        status: statusObj[d.status],
        orderUOM: d.orderUOM.label,
      })),
    };

    EditSOMutation.mutate(
      { orderId, req },
      {
        onSuccess: (data) => navigate("/outbound/so", { state: data.data.message }),
        onError,
      }
    );
    setClick(false)
  };

  const editStatus = EditSOMutation.status === "loading";

  return (
    <InboundLayout lg={12}>
      {(isFetching || editStatus) && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <SO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSaveButton url="/outbound/so" onClick={onSubmit} disabled={click || editStatus} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const [defaultVC, setDefaultVC] = useState(null);
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Cancel"],
      type: "actions",
    },
    ...soColumns,
  ];

  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);

  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || {
      ...detailValues,
      lineNo,
      vendorCode: defaultVC,
      status: "Open",
      newItem: true,
    },
    resolver: yupResolver(detailValidation),
  });

  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;

  const [vendorCode, skuCode] = watch(["vendorCode", "skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, vendorCode, skuCode },
    control,
    errors,
    setValue,
    getValues,
    setDefaultVC,
    mode: "EDIT",
  };

  const onSubmit = (data) => {
    if (skuValidation_(details, data)) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }
    if (formValues === null) {
      setDetails([...details, data]);
      setAlert(<Success content="Added" onChange={setAlert} />);
      reset();
    } else {
      setDetails(updateDetail(details, data));

      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };

  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));

            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };

  const onDelete = (data) => {
    if (data.newItem === false) return;
    setDetails(deleteNewDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const onCancel = (data) => {
    if (data.newItem === true) return;
    setDetails(cancelDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Cancelled" onChange={setAlert} />);
  };

  const totalOrderQuantity = getTotalQuantity(details, "orderQty");

  const totalQuantity = getTotalQuantity(details, "quantity");

  const caption = (
    <div>
      <span>Total Rows: {details.length}</span>
      <span style={{ marginLeft: "3rem" }}>Total Order Quantity: {totalOrderQuantity}</span>
      <span style={{ marginLeft: "3rem" }}>Total Quantity: {totalQuantity}</span>
    </div>
  );

  const tableProps = {
    statusLabel: "SO",
    columns,
    rows: details,
    caption,
    onRowClick,
    onDelete,
    onCancel,
    mode: "EDIT",
  };

  return (
    <InboundHeaderCard>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton
            disabled={formValues.status === "Cancel" || !isDirty}
            onClick={() => setFormValues(null)}
          />
        )}
      </form>
      <BasicTable2 {...tableProps} />
    </InboundHeaderCard>
  );
};

export default EditSO;
